/*
 * <slideshow-hero>: background video control for the slideshow hero.
 * - Plays only the video that is displayed (desktop or mobile) on the active slide, while the
 *   section is on screen; everything else stays paused (and unloaded thanks to preload="none").
 * - Respects prefers-reduced-motion (videos stay on their poster frame).
 * - Sound toggle buttons ([data-hero-sound]) mute/unmute the videos of their slide.
 * The slider itself is <vesper-slider> (assets/vesper.js).
 */
(() => {
  if (customElements.get('slideshow-hero')) return;

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mobileQuery = window.matchMedia('(max-width: 749px)');

  class SlideshowHero extends HTMLElement {
    connectedCallback() {
      this.sync = this.sync.bind(this);
      this.onClick = this.onClick.bind(this);
      this.inView = false;
      this.addEventListener('click', this.onClick);
      if (!this.querySelector('video')) return;
      // slider:change does not bubble, but it still passes through ancestors in the capture phase.
      this.addEventListener('slider:change', this.sync, true);
      mobileQuery.addEventListener('change', this.sync);
      reducedMotion.addEventListener('change', this.sync);
      this.observer = new IntersectionObserver(
        ([entry]) => {
          this.inView = entry.isIntersecting;
          this.sync();
        },
        { threshold: 0.1 }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('slider:change', this.sync, true);
      mobileQuery.removeEventListener('change', this.sync);
      reducedMotion.removeEventListener('change', this.sync);
      this.observer?.disconnect();
    }

    isActiveSlide(slide) {
      if (!slide || slide.hasAttribute('data-clone')) return false;
      const slider = this.querySelector('vesper-slider');
      if (!slider || !slider.active) return true;
      return slide.hasAttribute('data-visible');
    }

    sync() {
      this.querySelectorAll('video.vhero__video').forEach((video) => {
        const slide = video.closest('.vhero__slide');
        const displayed = video.getClientRects().length > 0;
        const allowed = !reducedMotion.matches || video.dataset.userStarted === 'true';
        const shouldPlay = this.inView && displayed && allowed && this.isActiveSlide(slide);
        if (shouldPlay && video.paused) {
          video.play().catch(() => {});
        } else if (!shouldPlay && !video.paused) {
          video.pause();
        }
      });
    }

    onClick(event) {
      const button = event.target.closest('[data-hero-sound]');
      if (!button || !this.contains(button)) return;
      const slide = button.closest('.vhero__slide');
      const unmute = button.getAttribute('data-sound-on') !== 'true';
      slide.querySelectorAll('video').forEach((video) => {
        video.muted = !unmute;
        if (unmute) video.dataset.userStarted = 'true';
      });
      button.setAttribute('data-sound-on', String(unmute));
      button.setAttribute('aria-label', unmute ? button.dataset.labelOff : button.dataset.labelOn);
      this.sync();
    }
  }

  customElements.define('slideshow-hero', SlideshowHero);
})();
